// Web Push (VAPID) subscribe & rotate (docs/01-architecture.md §6,
// docs/06-clients.md §4). The subscription is registered against the current
// session via `PATCH /sessions/current` with the @merkury/protocol
// UpdateSessionRequest shape — push_kind "webpush", payload = the
// PushSubscription JSON (opaque to the protocol).
//
// Rotation strategy: browsers may invalidate a subscription at any time
// (`pushsubscriptionchange`). The SW cannot PATCH the API itself (it has no
// access token), so it broadcasts PUSH_ROTATE to open windows; additionally
// `syncPushSubscription` runs on every app start, making rotation eventually
// consistent even when no window was open when the change happened.

import { UpdateSessionRequest } from '@merkury/protocol';

// --- Injected platform surfaces ----------------------------------------------

export interface PushSubscriptionLike {
  /** Raw `applicationServerKey` the subscription was created with, or null. */
  readonly options: { readonly applicationServerKey: ArrayBuffer | null };
  toJSON(): unknown;
  unsubscribe(): Promise<boolean>;
}

export interface PushManagerLike {
  getSubscription(): Promise<PushSubscriptionLike | null>;
  subscribe(options: {
    userVisibleOnly: boolean;
    applicationServerKey: Uint8Array;
  }): Promise<PushSubscriptionLike>;
}

export interface WebPushDeps {
  pushManager: PushManagerLike;
  /** Server VAPID public key, base64url (from build/runtime config). */
  vapidPublicKey: string;
  /** Current Notification.permission. */
  permission: () => 'default' | 'granted' | 'denied';
  /** `PATCH /sessions/current` — wired to the REST client at integration. */
  updateSession: (body: UpdateSessionRequest) => Promise<void>;
}

// --- Helpers ------------------------------------------------------------------

/** Standard base64url → bytes conversion for `applicationServerKey`. */
export function vapidKeyToBytes(base64Url: string): Uint8Array {
  const padded = base64Url + '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  return bytes;
}

function sameKey(current: ArrayBuffer | null, wanted: Uint8Array): boolean {
  if (current === null) return false;
  const a = new Uint8Array(current);
  if (a.length !== wanted.length) return false;
  for (let i = 0; i < a.length; i += 1) if (a[i] !== wanted[i]) return false;
  return true;
}

export type PushSyncResult =
  | 'subscribed' // fresh or rotated subscription registered with the server
  | 'refreshed' // existing valid subscription re-registered (idempotent)
  | 'cleared' // permission revoked: unsubscribed and cleared server-side
  | 'unavailable'; // no permission and nothing to clear

// --- Subscribe / rotate ---------------------------------------------------------

/**
 * Bring the browser subscription and the server session into agreement.
 * Safe to call on every app start and on PUSH_ROTATE messages from the SW.
 */
export async function syncPushSubscription(deps: WebPushDeps): Promise<PushSyncResult> {
  const existing = await deps.pushManager.getSubscription();

  if (deps.permission() !== 'granted') {
    if (existing) {
      await existing.unsubscribe();
      await deps.updateSession(UpdateSessionRequest.parse({ push: null }));
      return 'cleared';
    }
    return 'unavailable';
  }

  const wantedKey = vapidKeyToBytes(deps.vapidPublicKey);

  let subscription = existing;
  let rotated = false;
  if (subscription && !sameKey(subscription.options.applicationServerKey, wantedKey)) {
    // Server VAPID key changed: the old subscription can't be used — rotate.
    await subscription.unsubscribe();
    subscription = null;
    rotated = true;
  }
  if (!subscription) {
    subscription = await deps.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: wantedKey,
    });
  }

  await deps.updateSession(
    UpdateSessionRequest.parse({
      push: { kind: 'webpush', payload: JSON.stringify(subscription.toJSON()) },
    }),
  );
  return rotated || !existing ? 'subscribed' : 'refreshed';
}

/** Unsubscribe locally and clear the session's push subscription. */
export async function disablePush(deps: WebPushDeps): Promise<void> {
  const existing = await deps.pushManager.getSubscription();
  if (existing) await existing.unsubscribe();
  await deps.updateSession(UpdateSessionRequest.parse({ push: null }));
}
